import { client } from '@/services/api/axios.ts';
import { useQuery } from '@tanstack/react-query';

type FieldType =
  | 'number'
  | 'text'
  | 'email'
  | 'password'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'time'
  | 'datetime'
  | 'range'
  | 'color'
  | 'file';

interface FieldOption {
  value: string | number | boolean;
  label: string;
  disabled?: boolean;
}

interface FieldDefinition {
  type: FieldType;
  label: string;
  description?: string;
  placeholder?: string;
  default: unknown;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  options?: Array<string | FieldOption>;
  unit?: string;
  dependsOn?: {
    field: string;
    value: unknown;
  };
}

interface FormAction {
  label: string;
  action?: 'reset';
  type?: 'submit' | 'button';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

interface FormSchema {
  id: string;
  title: string;
  description?: string;
  enabled: boolean;
  status: string;
  inputs: Record<string, FieldDefinition>;
  actions?: Record<string, FormAction>;
}

export type GetPlantControlPanelScriptsResponse = {
  scripts: FormSchema[];
  isFresh: boolean;
};

export const useGetControlPanelScriptsPlantQuery = ({ plantId }: { plantId?: string }) => {
  return useQuery({
    queryKey: ['plants', plantId, 'control-panel', 'scripts'],
    queryFn: async () => {
      if (!plantId) return;

      const { data } = await client.get<{
        body: GetPlantControlPanelScriptsResponse;
      }>(`/plants/${plantId}/control-panel`, {
        params: {
          scripts: true,
        },
      });

      return data.body;
    },
    retry: 1,
    staleTime: 1000 * 15,
    refetchInterval: 1000 * 15,
  });
};
